import type {
  CoreExactMutation,
  CoreRead,
  PlatformRefusal,
  IdentityResponseDELETEMeSessionsSessionId,
  IdentityResponseGETMeProfile,
  IdentityResponseGETMeSessions,
  IdentityResponsePOSTAuth2faDeleteSecret,
  IdentityResponsePOSTAuth2faDisable,
  IdentityResponsePOSTAuth2faEnable,
  IdentityResponsePOSTAuth2faGenerate,
  IdentityResponsePOSTMeCredential,
  IdentityResponsePOSTMeSessionsRevokeAll,
  IdentityResponsePUTMeProfile,
} from '@exylon/generated-contract-kernel/types';

/** Generated operation discriminants, never a URL or a transport implementation. */
export const ACCOUNT_OPERATION_NAMES = Object.freeze({
  readProfile: 'GET /me/profile',
  updateProfile: 'PUT /me/profile',
  updateCredential: 'POST /me/credential',
  generateSecondFactor: 'POST /auth/2fa/generate',
  enableSecondFactor: 'POST /auth/2fa/enable',
  disableSecondFactor: 'POST /auth/2fa/disable',
  deleteSecondFactorSecret: 'POST /auth/2fa/delete-secret',
  readSessions: 'GET /me/sessions',
  revokeSession: 'DELETE /me/sessions/:sessionId',
  revokeAllSessions: 'POST /me/sessions/revoke-all',
} as const);

type AccountReadProfileCommand = Extract<CoreRead, { operation: typeof ACCOUNT_OPERATION_NAMES.readProfile }>;
type AccountUpdateProfileCommand = Extract<CoreExactMutation, { operation: typeof ACCOUNT_OPERATION_NAMES.updateProfile }>;
type AccountUpdateCredentialCommand = Extract<CoreExactMutation, { operation: typeof ACCOUNT_OPERATION_NAMES.updateCredential }>;
type AccountGenerateSecondFactorCommand = Extract<CoreExactMutation, { operation: typeof ACCOUNT_OPERATION_NAMES.generateSecondFactor }>;
type AccountEnableSecondFactorCommand = Extract<CoreExactMutation, { operation: typeof ACCOUNT_OPERATION_NAMES.enableSecondFactor }>;
type AccountDisableSecondFactorCommand = Extract<CoreExactMutation, { operation: typeof ACCOUNT_OPERATION_NAMES.disableSecondFactor }>;
type AccountDeleteSecondFactorSecretCommand = Extract<CoreExactMutation, { operation: typeof ACCOUNT_OPERATION_NAMES.deleteSecondFactorSecret }>;
type AccountReadSessionsCommand = Extract<CoreRead, { operation: typeof ACCOUNT_OPERATION_NAMES.readSessions }>;
type AccountRevokeSessionCommand = Extract<CoreExactMutation, { operation: typeof ACCOUNT_OPERATION_NAMES.revokeSession }>;
type AccountRevokeAllSessionsCommand = Extract<CoreExactMutation, { operation: typeof ACCOUNT_OPERATION_NAMES.revokeAllSessions }>;

type GeneratedCommands = {
  readProfile: AccountReadProfileCommand;
  updateProfile: AccountUpdateProfileCommand;
  updateCredential: AccountUpdateCredentialCommand;
  generateSecondFactor: AccountGenerateSecondFactorCommand;
  enableSecondFactor: AccountEnableSecondFactorCommand;
  disableSecondFactor: AccountDisableSecondFactorCommand;
  deleteSecondFactorSecret: AccountDeleteSecondFactorSecretCommand;
  readSessions: AccountReadSessionsCommand;
  revokeSession: AccountRevokeSessionCommand;
  revokeAllSessions: AccountRevokeAllSessionsCommand;
};
type MissingGeneratedCommand = {
  [Name in keyof GeneratedCommands]: [GeneratedCommands[Name]] extends [never] ? Name : never;
}[keyof GeneratedCommands];
type AssertNoMissingCommand<Missing extends never> = Missing;
type AccountGeneratedCommandProof = AssertNoMissingCommand<MissingGeneratedCommand>;

export type AccountProfile = IdentityResponseGETMeProfile['result']['result'];
type AccountProfileUpdateResult = IdentityResponsePUTMeProfile['result'];
type AccountCredentialResult = IdentityResponsePOSTMeCredential['result'];
type AccountSessionsResult = IdentityResponseGETMeSessions['result'];
export type AccountSession = AccountSessionsResult['sessions'][number];
export type AccountSessionRevocationResult = IdentityResponseDELETEMeSessionsSessionId['result'];
type AccountAllSessionsRevocationResult = IdentityResponsePOSTMeSessionsRevokeAll['result'];
export type AccountSecondFactorSetup = IdentityResponsePOSTAuth2faGenerate['result'];
type AccountSecondFactorEnableResult = IdentityResponsePOSTAuth2faEnable['result'];
type AccountSecondFactorDisableResult = IdentityResponsePOSTAuth2faDisable['result'];
type AccountSecondFactorDeleteResult = IdentityResponsePOSTAuth2faDeleteSecret['result'];

export interface AccountCanonicalRefusal {
  readonly kind: 'canonical_refusal';
  readonly reason: 'authorization_denied' | 'conflict' | 'invalid_request' | 'not_found' | 'unknown';
  readonly error: PlatformRefusal;
  readonly retry: 'after_refresh' | 'after_permission_change' | 'manual_only';
}

export type AccountRuntimeResult<Canonical> =
  | { readonly kind: 'success'; readonly canonical: Canonical }
  | AccountCanonicalRefusal;

/** Local pre-dispatch pause: no HTTP request, denial or receipt is asserted. */
export class AccountVerificationPendingError extends Error {
  readonly kind = 'verification_pending';
  readonly status = null;
  readonly canonical = null;

  constructor() {
    super('Account dispatch is paused until session verification completes.');
    this.name = 'AccountVerificationPendingError';
  }
}

export type AccountMutationOperation = Exclude<
  (typeof ACCOUNT_OPERATION_NAMES)[keyof typeof ACCOUNT_OPERATION_NAMES],
  typeof ACCOUNT_OPERATION_NAMES.readProfile | typeof ACCOUNT_OPERATION_NAMES.readSessions
>;
export type AccountIdempotencyKeyFactory = (operation: AccountMutationOperation) => string;

/**
 * H10 owns generated transport, validation and response unwrapping. The account
 * foundation consumes canonical results and builds only generated commands.
 * No principal, restaurant or franchise selector is supplied for self operations.
 */
export interface AccountRuntimePort {
  readProfile(command: AccountReadProfileCommand): Promise<AccountRuntimeResult<AccountProfile>>;
  updateProfile(command: AccountUpdateProfileCommand): Promise<AccountRuntimeResult<AccountProfileUpdateResult>>;
  updateCredential(command: AccountUpdateCredentialCommand): Promise<AccountRuntimeResult<AccountCredentialResult>>;
  generateSecondFactor(command: AccountGenerateSecondFactorCommand): Promise<AccountRuntimeResult<AccountSecondFactorSetup>>;
  enableSecondFactor(command: AccountEnableSecondFactorCommand): Promise<AccountRuntimeResult<AccountSecondFactorEnableResult>>;
  disableSecondFactor(command: AccountDisableSecondFactorCommand): Promise<AccountRuntimeResult<AccountSecondFactorDisableResult>>;
  deleteSecondFactorSecret(command: AccountDeleteSecondFactorSecretCommand): Promise<AccountRuntimeResult<AccountSecondFactorDeleteResult>>;
  readSessions(command: AccountReadSessionsCommand): Promise<AccountRuntimeResult<AccountSessionsResult>>;
  revokeSession(command: AccountRevokeSessionCommand): Promise<AccountRuntimeResult<AccountSessionRevocationResult>>;
  revokeAllSessions(command: AccountRevokeAllSessionsCommand): Promise<AccountRuntimeResult<AccountAllSessionsRevocationResult>>;
}
